import React, { useState } from 'react';
import { cn } from '@/lib/utils';
import { useTrendingData, TrendingFilter as TrendingFilterType } from '@/hooks/useTrendingData';
import TrendingFilter from '@/components/TrendingFilter';
import SolutionCard from '@/components/SolutionCard';
import { TrendingUp, Sparkles, AlertCircle } from 'lucide-react';

interface NewAndTrendingSectionProps {
  className?: string;
}

const TRENDING_FILTER_STORAGE_KEY = 'trending-filter-selection';

const NewAndTrendingSection: React.FC<NewAndTrendingSectionProps> = ({ className }) => {
  // Initialize from localStorage or default to 'new'
  const [selectedFilter, setSelectedFilter] = useState<TrendingFilterType>(() => {
    try {
      const saved = localStorage.getItem(TRENDING_FILTER_STORAGE_KEY);
      return (saved as TrendingFilterType) || 'new';
    } catch {
      return 'new';
    }
  });

  // Save filter selection to localStorage whenever it changes
  const handleFilterChange = (filter: TrendingFilterType) => {
    setSelectedFilter(filter);
    try {
      localStorage.setItem(TRENDING_FILTER_STORAGE_KEY, filter);
    } catch {
      // Ignore localStorage errors (e.g., in private browsing mode)
    }
  };
  
  const { solutions, isLoading, isError, error } = useTrendingData(selectedFilter);

  // Get section title and icon based on selected filter
  const getSectionInfo = () => {
    switch (selectedFilter) {
      case 'new':
        return {
          title: 'New AI Solutions',
          subtitle: 'Latest solutions added to our platform',
          icon: <Sparkles className="h-6 w-6" />
        };
      case 'trending-week':
        return {
          title: 'Trending This Week',
          subtitle: 'Most clicked solutions in the past 7 days',
          icon: <TrendingUp className="h-6 w-6" />
        };
      case 'trending-month':
        return {
          title: 'Trending This Month',
          subtitle: 'Most clicked solutions in the past 30 days',
          icon: <TrendingUp className="h-6 w-6" />
        };
      default:
        return {
          title: 'New & Trending',
          subtitle: 'Discover the latest AI solutions',
          icon: <Sparkles className="h-6 w-6" />
        };
    }
  };

  const { title, subtitle, icon } = getSectionInfo();

  // Error state
  if (isError) {
    return (
      <section className={cn("py-16", className)}>
        <div className="container mx-auto px-4">
          <div className="text-center">
            <div className="flex items-center justify-center gap-2 text-destructive mb-4">
              <AlertCircle className="h-5 w-5" />
              <span>Unable to load trending solutions</span>
            </div>
            <p className="text-sm text-muted-foreground">
              {error?.message || 'Please try again later.'}
            </p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className={cn("py-16 bg-gradient-to-br from-background to-secondary/20", className)}>
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-6">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="text-primary">
              {icon}
            </div>
            <h2 className="text-3xl font-bold">{title}</h2>
          </div>
        </div>

        {/* Filter Controls */}
        <div className="flex justify-center mb-8">
          <TrendingFilter
            selectedFilter={selectedFilter}
            onFilterChange={handleFilterChange}
            className="justify-center"
          />
        </div>

        {/* Solutions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
          {isLoading ? (
            // Loading skeleton
            Array.from({ length: 3 }).map((_, index) => (
              <div
                key={index}
                className={cn(
                  "rounded-xl overflow-hidden bg-card border border-border/40",
                  "animate-pulse"
                )}
              >
                <div className="aspect-video bg-secondary/50" />
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-secondary/50 rounded w-1/3" />
                  <div className="h-6 bg-secondary/50 rounded w-3/4" />
                  <div className="space-y-2">
                    <div className="h-3 bg-secondary/50 rounded" />
                    <div className="h-3 bg-secondary/50 rounded w-5/6" />
                  </div>
                  <div className="flex gap-2">
                    <div className="h-5 bg-secondary/50 rounded w-16" />
                    <div className="h-5 bg-secondary/50 rounded w-20" />
                  </div>
                </div>
              </div>
            ))
          ) : solutions.length > 0 ? (
            // Actual solutions
            solutions.map((solution) => (
              <SolutionCard
                key={solution.id}
                {...solution}
              />
            ))
          ) : (
            // Empty state
            <div className="col-span-full text-center py-12">
              <div className="text-muted-foreground mb-2">
                <Sparkles className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <h3 className="text-lg font-medium mb-2">No solutions found</h3>
                <p className="text-sm">
                  {selectedFilter === 'new' 
                    ? 'No new solutions have been added yet.'
                    : 'No trending solutions for this period.'
                  }
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default NewAndTrendingSection; 